/** Shown when shorthand input is wrong shape; keep in sync with RepoAnalyzer placeholder. */
const INPUT_HINT =
  "Paste a GitHub repo URL, pull request URL, or owner/repo (e.g. facebook/react).";

/**
 * Normalizes GitHub repository input for the repo submission flow.
 * Accepts owner/repo and common github.com URL shapes; rejects ambiguous or unsupported input.
 *
 * @param {unknown} raw
 * @returns {{ ok: true, owner: string, repo: string, branch?: string } | { ok: false, message: string }}
 */
export function parseGithubRepoInput(raw) {
  if (typeof raw !== "string") {
    return { ok: false, message: "Please enter a repository name" };
  }

  let s = raw.trim();
  if (!s) {
    return { ok: false, message: "Please enter a repository name" };
  }

  const q = s.indexOf("?");
  const h = s.indexOf("#");
  let cut = s.length;
  if (q >= 0) cut = Math.min(cut, q);
  if (h >= 0) cut = Math.min(cut, h);
  s = s.slice(0, cut).trim();
  s = s.replace(/\/+$/, "");

  const githubPathMatch = s.match(
    /^(?:https?:\/\/)?(?:www\.)?github\.com\/(.+)$/i
  );

  if (githubPathMatch) {
    const pathPart = githubPathMatch[1].replace(/\/+$/, "");
    const segments = pathPart.split("/").filter(Boolean);
    if (segments.length < 2) {
      return {
        ok: false,
        message: INPUT_HINT,
      };
    }
    let owner = segments[0];
    let repoName = segments[1];
    if (repoName.endsWith(".git")) {
      repoName = repoName.slice(0, -4);
    }

    let branch;
    let pullNumber;
    if (segments.length > 2) {
      const third = segments[2].toLowerCase();
      if (third === "pull" && segments.length >= 4 && /^\d+$/.test(segments[3])) {
        pullNumber = Number(segments[3]);
      } else if (third === "tree" && segments.length >= 4) {
        branch = segments.slice(3).join("/");
      } else if (third === "blob" && segments.length === 5) {
        // Simple /blob/{branch}/{file} only; multi-segment branch names are ambiguous in V1.
        branch = segments[3];
      } else if (third !== "tree" && third !== "blob" && third !== "pull") {
        return {
          ok: false,
          message:
            "That GitHub link isn’t supported here. Paste the repo home URL, a pull request URL, or owner/repo.",
        };
      }
    }

    if (
      !isPlausibleGithubSegment(owner) ||
      !isPlausibleGithubSegment(repoName)
    ) {
      return { ok: false, message: "Invalid owner or repository name." };
    }

    if (branch && !isPlausibleBranchRef(branch)) {
      return { ok: false, message: "Invalid branch or ref in GitHub URL." };
    }

    return {
      ok: true,
      owner,
      repo: repoName,
      ...(pullNumber ? { kind: "pull", pullNumber } : {}),
      ...(branch ? { branch } : {}),
    };
  }

  if (/^https?:\/\//i.test(s)) {
    return {
      ok: false,
      message:
        "Only github.com links are supported. Paste a GitHub repo URL or owner/repo.",
    };
  }

  const slashCount = (s.match(/\//g) || []).length;
  if (slashCount !== 1) {
    return {
      ok: false,
      message: INPUT_HINT,
    };
  }

  const [owner, repoPart] = s.split("/");
  let repo = repoPart;
  if (!owner || !repo) {
    return {
      ok: false,
      message: INPUT_HINT,
    };
  }
  if (repo.endsWith(".git")) {
    repo = repo.slice(0, -4);
  }
  if (/^github\.com$/i.test(owner)) {
    return {
      ok: false,
      message:
        "Paste github.com/owner/repo, https://github.com/owner/repo, or owner/repo (not github.com as the first segment).",
    };
  }
  if (!isPlausibleGithubSegment(owner) || !isPlausibleGithubSegment(repo)) {
    return { ok: false, message: "Invalid owner or repository name." };
  }
  return { ok: true, owner, repo };
}

/** Conservative validation: non-empty, no spaces, typical GitHub name characters. */
function isPlausibleGithubSegment(seg) {
  if (!seg || seg.length > 100) return false;
  if (/\s/.test(seg)) return false;
  return /^[a-zA-Z0-9._-]+$/.test(seg);
}

/** Branch/ref segments may include slashes; disallow control characters. */
function containsControlCharacters(value) {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function isPlausibleBranchRef(value) {
  if (!value || value.length > 256) return false;
  if (containsControlCharacters(value)) return false;
  return true;
}
